import { select } from "d3";

import {
  AppletElement,
  EDGE_STROKE,
  EdgeEndpoints,
  GroupSelection,
  NODE_STROKE,
  Point,
  SvgSelection,
  renderApplet,
  trimmedEdgeEndpoints,
} from "./common";

type GraphNode = Readonly<{
  id: string;
  label: string;
  x: number;
  y: number;
}>;

type GraphEdge = Readonly<{
  source: string;
  target: string;
}>;

type GraphSpec = Readonly<{
  title: string;
  width: number;
  height: number;
  fill: string;
  fillOpacity: number;
  nodes: readonly GraphNode[];
  edges: readonly GraphEdge[];
}>;

const NODE_RADIUS = 23;
const NODE_LABEL_FONT_SIZE = 18;
const EDGE_LABEL_FILL = "#8a8a8a";
const EDGE_LABEL_BACKGROUND_FILL = "white";
const EDGE_LABEL_BACKGROUND_RADIUS = 10;
const EDGE_LABEL_FONT_SIZE = 13;

const TITLE_TREE_GRAPH: GraphSpec = {
  title: "A graceful tree with six edges.",
  width: 500,
  height: 200,
  fill: "#8fbc8f",
  fillOpacity: 0.270588,
  nodes: [
    { id: "_0", label: "0", x: 26, y: 170 },
    { id: "_1", label: "1", x: 326, y: 92 },
    { id: "_2", label: "2", x: 112, y: 70 },
    { id: "_3", label: "3", x: 24, y: 34 },
    { id: "_4", label: "4", x: 222, y: 52 },
    { id: "_5", label: "5", x: 454, y: 142 },
    { id: "_6", label: "6", x: 196, y: 134 },
  ],
  edges: [
    { source: "_0", target: "_6" },
    { source: "_1", target: "_6" },
    { source: "_1", target: "_5" },
    { source: "_1", target: "_4" },
    { source: "_2", target: "_4" },
    { source: "_2", target: "_3" },
  ],
};

function nodeLookup(spec: GraphSpec): ReadonlyMap<string, GraphNode> {
  return new Map(spec.nodes.map((node) => [node.id, node]));
}

function edgeEndpoints(edge: GraphEdge, nodes: ReadonlyMap<string, GraphNode>): EdgeEndpoints {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return { x1: 0, y1: 0, x2: 0, y2: 0 };
  }

  return trimmedEdgeEndpoints(source, target, NODE_RADIUS);
}

function edgeLabelPosition(edge: GraphEdge, nodes: ReadonlyMap<string, GraphNode>): Point {
  const endpoints = edgeEndpoints(edge, nodes);
  const dx = endpoints.x2 - endpoints.x1;
  const dy = endpoints.y2 - endpoints.y1;
  const length = Math.hypot(dx, dy);
  if (length === 0) {
    return { x: endpoints.x1, y: endpoints.y1 };
  }

  return {
    x: (endpoints.x1 + endpoints.x2) * 0.5,
    y: (endpoints.y1 + endpoints.y2) * 0.5,
  };
}

function gracefulEdgeLabel(edge: GraphEdge, nodes: ReadonlyMap<string, GraphNode>): string {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return "";
  }

  const sourceLabel = Number.parseInt(source.label, 10);
  const targetLabel = Number.parseInt(target.label, 10);
  if (Number.isNaN(sourceLabel) || Number.isNaN(targetLabel)) {
    return "";
  }

  return Math.abs(sourceLabel - targetLabel).toString();
}

function appendSvg(element: AppletElement, spec: GraphSpec): SvgSelection {
  return select<AppletElement, unknown>(element)
    .append<SVGSVGElement>("svg")
    .attr("viewBox", `0 0 ${spec.width} ${spec.height}`)
    .attr("width", spec.width)
    .attr("height", spec.height)
    .attr("role", "img")
    .attr("aria-label", spec.title);
}

function renderEdges(group: GroupSelection, spec: GraphSpec): void {
  const nodes = nodeLookup(spec);

  group.append<SVGGElement>("g")
    .attr("class", "graceful-d3-edges")
    .selectAll<SVGLineElement, GraphEdge>("line")
    .data(spec.edges)
    .join("line")
    .attr("x1", (edge: GraphEdge): number => edgeEndpoints(edge, nodes).x1)
    .attr("y1", (edge: GraphEdge): number => edgeEndpoints(edge, nodes).y1)
    .attr("x2", (edge: GraphEdge): number => edgeEndpoints(edge, nodes).x2)
    .attr("y2", (edge: GraphEdge): number => edgeEndpoints(edge, nodes).y2)
    .attr("stroke", EDGE_STROKE)
    .attr("stroke-width", 1);
}

function renderEdgeLabels(group: GroupSelection, spec: GraphSpec): void {
  const nodes = nodeLookup(spec);
  const labeledEdges = spec.edges;

  group.append<SVGGElement>("g")
    .attr("class", "graceful-d3-edge-label-backgrounds")
    .selectAll<SVGCircleElement, GraphEdge>("circle")
    .data(labeledEdges)
    .join("circle")
    .attr("cx", (edge: GraphEdge): number => edgeLabelPosition(edge, nodes).x)
    .attr("cy", (edge: GraphEdge): number => edgeLabelPosition(edge, nodes).y)
    .attr("r", EDGE_LABEL_BACKGROUND_RADIUS)
    .attr("fill", EDGE_LABEL_BACKGROUND_FILL);

  group.append<SVGGElement>("g")
    .attr("class", "graceful-d3-edge-labels")
    .selectAll<SVGTextElement, GraphEdge>("text")
    .data(labeledEdges)
    .join("text")
    .attr("x", (edge: GraphEdge): number => edgeLabelPosition(edge, nodes).x)
    .attr("y", (edge: GraphEdge): number => edgeLabelPosition(edge, nodes).y)
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("font-family", "Helvetica, sans-serif")
    .attr("font-size", EDGE_LABEL_FONT_SIZE)
    .attr("fill", EDGE_LABEL_FILL)
    .text((edge: GraphEdge): string => gracefulEdgeLabel(edge, nodes));
}

function renderNodes(group: GroupSelection, spec: GraphSpec): void {
  const nodeGroups = group.append<SVGGElement>("g")
    .attr("class", "graceful-d3-nodes")
    .selectAll<SVGGElement, GraphNode>("g")
    .data(spec.nodes)
    .join("g")
    .attr("transform", (node: GraphNode): string => `translate(${node.x},${node.y})`);

  nodeGroups.append<SVGCircleElement>("circle")
    .attr("r", NODE_RADIUS)
    .attr("fill", spec.fill)
    .attr("fill-opacity", spec.fillOpacity)
    .attr("stroke", NODE_STROKE);

  nodeGroups.append<SVGTextElement>("text")
    .attr("text-anchor", "middle")
    .attr("dy", "0.3em")
    .attr("font-family", "Times, serif")
    .attr("font-size", NODE_LABEL_FONT_SIZE)
    .text((node: GraphNode): string => node.label);
}

function renderTitleTree(element: AppletElement): void {
  element.dataset.rendered = "true";
  element.replaceChildren();

  const graph = appendSvg(element, TITLE_TREE_GRAPH)
    .append<SVGGElement>("g")
    .attr("class", "graceful-d3-graph");

  renderEdges(graph, TITLE_TREE_GRAPH);
  renderEdgeLabels(graph, TITLE_TREE_GRAPH);
  renderNodes(graph, TITLE_TREE_GRAPH);
}

renderApplet(".graceful-graph-applet[data-graph-id=\"title-tree\"]", renderTitleTree);
